import React, { useState, useEffect } from 'react';
import apiClient from '../api/axiosConfig';
import { useWebSocket } from '../contexts/WebSocketContext';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface BulkUpdateProgress {
  id: string;
  status: string;
  progress: number;
  message: string;
  totalUsers: number;
  processedUsers: number;
}

const BulkUserUpdatePage: React.FC = () => {
  const [userIds, setUserIds] = useState<string>('');
  const [idType, setIdType] = useState<'normal' | 'previous_id' | 'external_id'>('normal');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [, setJobId] = useState<string | null>(null);

  // Get the WebSocket context
  const { socket } = useWebSocket();

  // Listen for bulk update progress events
  useEffect(() => {
    if (!socket) return;

    const handleBulkUpdateProgress = (data: BulkUpdateProgress) => {
      console.log('Bulk update progress:', data);

      // Update progress state
      setProgress({
        current: data.processedUsers,
        total: data.totalUsers
      });

      // Update status based on the progress
      if (data.status === 'completed') {
        setIsSubmitting(false);
        setSuccess(data.message);
      } else if (data.status === 'error') {
        setIsSubmitting(false);
        setError(data.message);
      }
    };

    // Add event listener
    socket.on('bulkUpdateProgress', handleBulkUpdateProgress);

    // Clean up on unmount
    return () => {
      socket.off('bulkUpdateProgress', handleBulkUpdateProgress);
    };
  }, [socket]);

  // Check for existing job status on mount
  useEffect(() => {
    const checkJobStatus = async () => {
      try {
        const response = await apiClient.get('/api/users/bulk-update-status');
        const status = response.data;

        // If there's an active job, update the UI
        if (status.status === 'started' || status.status === 'in_progress') {
          setIsSubmitting(true);
          setJobId(status.id);
          setProgress({
            current: status.processedUsers,
            total: status.totalUsers
          });
        } else if (status.status === 'completed' && status.id) {
          // If a job was recently completed, show the success message
          setSuccess(status.message);
        } else if (status.status === 'error' && status.id) {
          // If a job recently failed, show the error message
          setError(status.message);
        }
      } catch (error) {
        console.error('Failed to check job status:', error);
      }
    };

    checkJobStatus();
  }, []);

  // Parse user IDs from input
  const parseUserIds = (input: string): (number | string)[] => {
    // Split by commas, newlines, or spaces
    const idStrings = input.split(/[\s,]+/).filter(id => id.trim() !== '');

    // Process IDs based on the ID type
    if (idType === 'normal') {
      // Convert to numbers and filter out invalid IDs for normal type
      return idStrings
        .map(id => parseInt(id.trim(), 10))
        .filter(id => !isNaN(id) && id > 0);
    } else {
      // For external_id or previous_id, keep the original strings
      return idStrings.map(id => id.trim()).filter(id => id !== '');
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset states
    setError(null);
    setSuccess(null);
    setProgress(null);
    setJobId(null);

    // Parse user IDs
    const parsedIds = parseUserIds(userIds);

    if (parsedIds.length === 0) {
      setError('Please enter at least one valid user ID');
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the API endpoint
      const response = await apiClient.post('/api/users/bulk-update-notifications-activate', {
        userIds: parsedIds,
        idType
      });

      // Store the job ID
      setJobId(response.data.jobId);

      // Set initial progress
      setProgress({
        current: 0,
        total: response.data.totalUsers
      });

      // Clear the input field on success
      setUserIds('');
    } catch (error: any) {
      console.error('Failed to update users:', error);
      setError(error.response?.data?.error || 'Failed to update users. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            User Bulk Activation
          </h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500">
            <p>
              Enter a list of User IDs to activate their account and opt them out of any email notifications.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-5">
            <div className="w-full">
              <label htmlFor="userIds" className="block text-sm font-medium text-gray-700">
                User IDs <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <textarea
                  id="userIds"
                  name="userIds"
                  rows={8}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  placeholder="Enter user IDs separated by commas, spaces, or new lines"
                  value={userIds}
                  onChange={(e) => setUserIds(e.target.value)}
                  required
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                You can enter any number of user IDs. The IDs can be separated by commas, spaces, or new lines. The system will process them in batches.
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                ID Type
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm">
                  <button
                    type="button"
                    onClick={() => setIdType('external_id')}
                    className={`relative inline-flex items-center px-4 py-2 rounded-l-md border ${
                      idType === 'external_id'
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary`}
                  >
                    External ID
                  </button>
                  <button
                    type="button"
                    onClick={() => setIdType('normal')}
                    className={`relative -ml-px inline-flex items-center px-4 py-2 border ${
                      idType === 'normal'
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary`}
                  >
                    Normal ID
                  </button>
                  <button
                    type="button"
                    onClick={() => setIdType('previous_id')}
                    className={`relative -ml-px inline-flex items-center px-4 py-2 rounded-r-md border ${
                      idType === 'previous_id'
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary`}
                  >
                    Previous ID
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Select the type of ID you are providing. Normal ID is the default Hivebrite user ID.
              </p>
            </div>

            <div className="mt-5">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`inline-flex items-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting && (
                  <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                )}
                {isSubmitting ? 'Processing...' : 'Update Users'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Progress indicator */}
      {progress && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-md font-medium text-gray-900">Processing Users</h3>
            <div className="mt-2">
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold inline-block text-primary">
                      {Math.round((progress.current / progress.total) * 100)}%
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-primary">
                      {progress.current}/{progress.total}
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                  <div
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {error}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                {success}
              </h3>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUserUpdatePage;
